import React, { useEffect, useState } from "react";
import "./UserBranches.scss";
import { useLocation, useNavigate } from "react-router-dom";
import ROUTES from "../../../constants/routes";
import {
  deleteUserAccount,
  fetchUser,
  updateUserBranches,
} from "../../../api/userManagement";
import Loader from "../../../components/Loader/Loader";
import CustomButton from "../../../components/CustomButton/CustomButton";
import ToggleSwitch from "../../../components/ToggleSwitch/ToggleSwitch";
import { useForm } from "react-hook-form";

import { PiGitBranchDuotone } from "react-icons/pi";
import { ImProfile } from "react-icons/im";
import { IoKeyOutline } from "react-icons/io5";
import { FaTrash } from "react-icons/fa";
import { IoMailOutline } from "react-icons/io5";

import Sales from "../../Sales/Sales";
import Orders from "../../Orders/Orders";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import InputSelect from "../../../components/InputSelect/InputSelect";
import {
  fetchPermissions,
  syncUserPermissions,
} from "../../../api/permissions";
import {
  clearUser,
  updateUserPermissions,
} from "../../../redux/features/user-slice";
import { clearAnalytics } from "../../../redux/features/analytics-slice";
import { clearBranches } from "../../../redux/features/branch-slice";
import { clearCart } from "../../../redux/features/cart-slice";
import { clearHomeData } from "../../../redux/features/home-slice";
import { clearOrganization } from "../../../redux/features/organization-slice";
import { clearSubscriptionPlans } from "../../../redux/features/subscription-slice";
import { Capacitor } from "@capacitor/core";
import ConfirmAdminDelete from "../../../components/ConfirmAdminDelete/ConfirmAdminDelete";
import useUserIsAllowed from "../../../helpers/userAuthorized";
import { clearProducts } from "../../../redux/features/products-slice";
import { storage } from "../../../helpers/storage";

const IMAGE_URL = process.env.REACT_APP_IMAGE_URL;

const UserBranches = ({
  desktopView,
  selected,
  setSelectedDesktopTag,
  replace,
  reload,
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const selectedTabInfo = location.state?.selectedTab || "info";
  const [userDetails, setUserDetails] = useState({});
  const [userPermissions, setUserPermissions] = useState([]);
  const [permissions, setPermissions] = useState([]);

  const [openDeleteModel, setOpenDeleteModel] = useState(false);
  const [openManageBranch, setOpenManageBranch] = useState(false);

  const [loading, setLoading] = useState(false);

  const [selectedTab, setSelectedTab] = useState(selectedTabInfo);

  const userIsAllowedToAddEditUser = useUserIsAllowed("administrator");

  useEffect(() => {
    const userData = desktopView ? selected : location.state?.userData || {};
    if (!userData?.id) {
      !desktopView && navigate(ROUTES.manageUser.path);
    } else {
      setUserDetails(userData);
      if (userData?.permissions?.length > 0) {
        setUserPermissions(userData?.permissions);
      } else {
        setUserPermissions([]);
      }
    }
  }, [location, selected]);

  const getPermissions = () => {
    setLoading(true);

    fetchPermissions()
      .then((res) => {
        if (res.data?.permissions?.length > 0) {
          setPermissions(res.data?.permissions);
        } else {
          setPermissions([]);
        }
      })
      .catch((err) => {
        console.log("error");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    getPermissions();
  }, []);

  return (
    <>
      {loading && <Loader />}
      <div className="user-details">
        <div className="top-bar">
          {["info", "permission", "sales", "orders"].map((el, i) => {
            return (
              <p
                className={`tab ${selectedTab === el ? "active" : ""}`}
                key={i}
                onClick={() => {
                  if (el === "permission" && !userIsAllowedToAddEditUser) {
                    return toast.dismiss(); toast.info(
                      "You do not have sufficient permissions to perform this action. Please contact your administrator."
                    );
                  }
                  if (selectedTab !== el) {
                    setSelectedTab(el);
                  }
                }}
              >
                {el}
              </p>
            );
          })}
        </div>
        <div
          className={`main-content container-wrapper ${
            selectedTab === "sales" || selectedTab === "orders" ? "hidden" : ""
          }`}
        >
          {selectedTab === "info" && (
            <UserInfo
              userDetails={userDetails}
              desktopView={desktopView}
              setSelectedDesktopTag={setSelectedDesktopTag}
              setOpenDeleteModel={setOpenDeleteModel}
              setOpenManageBranch={setOpenManageBranch}
            />
          )}

          {selectedTab === "permission" && (
            <UserPermissions
              setLoading={setLoading}
              permissions={permissions}
              userDetails={userDetails}
              userPermissions={userPermissions}
              setUserPermissions={setUserPermissions}
              reload={(data) => {
                reload && reload(data);
              }}
            />
          )}
          {selectedTab === "sales" && <UserSales userDetails={userDetails} />}
          {selectedTab === "orders" && <UserOrders userDetails={userDetails} />}
        </div>
        {(openDeleteModel || openManageBranch) && (
          <div
            className="blur-back"
            onClick={() => {
              setOpenDeleteModel(false);
              setOpenManageBranch(false);
            }}
          ></div>
        )}
        <DeleteModel
          open={openDeleteModel}
          setOpen={setOpenDeleteModel}
          selectedUser={userDetails}
          reload={(data) => {
            replace && replace(data);
          }}
        />
        <ManageUserBranches
          open={openManageBranch}
          setOpen={setOpenManageBranch}
          selectedUser={userDetails}
          setUserDetails={setUserDetails}
          reload={(data) => {
            reload && reload(data);
          }}
        />
      </div>
    </>
  );
};

export default UserBranches;

const UserInfo = ({
  userDetails,
  desktopView,
  setSelectedDesktopTag,
  setOpenDeleteModel,
  setOpenManageBranch,
}) => {
  const navigate = useNavigate();

  const userIsAllowedToAddEditUser = useUserIsAllowed("administrator");

  return (
    <>
      <div className="user-info">
        <div className="info">
          <img
            src={
              userDetails?.image
                ? `${IMAGE_URL}${userDetails?.image}`
                : "/images/profile.svg"
            }
            alt=""
            aria-label={
              userDetails?.image
                ? `${userDetails?.name || "User"}'s Profile Image`
                : `${userDetails?.name || "User"}'s Default Profile Image`
            }
          />

          <div className="details">
            <p className="name">{userDetails?.name || "Username"}</p>
            <p className="email">
              <IoMailOutline /> {userDetails?.email || "Email"}
            </p>
            <div
              className={`status-wrapper ${
                userDetails?.status ? "active" : ""
              }`}
            >
              <p className="status">
                <span className="circle"></span>
                Status:{" "}
                <span>{userDetails?.status ? "Active" : "Inactive"}</span>
              </p>
              <p className="branch">
                <PiGitBranchDuotone /> Current Branch:{" "}
                {userDetails?.branches?.length > 0
                  ? userDetails?.branches[0]?.name
                  : "No Branch"}
              </p>
            </div>
          </div>
        </div>

        <div className="buttons">
          <button
            type="button"
            onClick={() => {
              if (userIsAllowedToAddEditUser) {
                if (desktopView) {
                  setSelectedDesktopTag && setSelectedDesktopTag("Edit User");
                } else {
                  navigate(ROUTES.editUser.path, {
                    state: { editData: userDetails },
                  });
                }
              } else {
                toast.dismiss(); toast.info(
                  "You do not have sufficient permissions to perform this action. Please contact your administrator."
                );
              }
            }}
            className="normal"
          >
            <ImProfile /> Edit Profile
          </button>
          <button
            type="button"
            onClick={() => {
              if (userIsAllowedToAddEditUser) {
                if (desktopView) {
                  setSelectedDesktopTag &&
                    setSelectedDesktopTag("Reset Password");
                  navigate("#", {
                    state: {
                      userId: userDetails?.id,
                      userDetails: userDetails,
                    },
                  });
                } else {
                  navigate(ROUTES.changeUserPassword.path, {
                    state: {
                      userId: userDetails?.id,
                      userDetails: userDetails,
                    },
                  });
                }
              } else {
                toast.dismiss(); toast.info(
                  "You do not have sufficient permissions to perform this action. Please contact your administrator."
                );
              }
            }}
            className="normal"
          >
            <IoKeyOutline /> Change Password
          </button>
          {!userDetails?.is_admin && (
            <button
              type="button"
              onClick={() => {
                if (userIsAllowedToAddEditUser) {
                  setOpenManageBranch(true);
                } else {
                  toast.dismiss(); toast.info(
                    "You do not have sufficient permissions to perform this action. Please contact your administrator."
                  );
                }
              }}
              className="normal"
            >
              <PiGitBranchDuotone /> Change Branch
            </button>
          )}
          <button
            type="button"
            onClick={() => {
              if (userIsAllowedToAddEditUser) {
                setOpenDeleteModel(true);
              } else {
                toast.dismiss(); toast.info(
                  "You do not have sufficient permissions to perform this action. Please contact your administrator."
                );
              }
            }}
            className="normal danger"
          >
            <FaTrash /> Delete User
          </button>
        </div>
      </div>
    </>
  );
};

const UserPermissions = ({
  setLoading,
  permissions,
  userPermissions,
  setUserPermissions,
  userDetails,
  reload,
}) => {
  const dispatch = useDispatch();

  const { id: loggedUserId } = useSelector((state) => state.userReducer.value);

  const { register, handleSubmit, setValue } = useForm();

  const userIsAllowedToAddEditUser = useUserIsAllowed("administrator");

  const handleSubmitUserPermissions = (data) => {
    setLoading(true);
    const permission_ids = Object.keys(data).filter((id) => data[id] === true);

    let newPermissions = permissions?.map((el) => {
      const exists = permission_ids?.find((item) => item === `${el?.id}`);

      if (exists) {
        return el;
      } else {
        return null;
      }
    });

    newPermissions = newPermissions.filter((el) => el !== null);

    syncUserPermissions(userDetails?.id, { permission_ids: permission_ids })
      .then((res) => {
        toast.dismiss(); toast.success("User's permissions updated.");
        if (userDetails?.id === loggedUserId) {
          dispatch(updateUserPermissions({ permissions: newPermissions }));
        }
        setUserPermissions(newPermissions);
        reload && reload({ ...userDetails, permissions: newPermissions });
      })
      .catch((err) => {
        if (err?.response?.data?.message) {
          if (err?.response?.data?.message === "User not authorized!") {
            toast.dismiss(); toast.error(
              "You do not have sufficient permissions to perform this action. Please contact your administrator."
            );
          } else {
            toast.dismiss(); toast.error(`${err?.response?.data?.message}`);
          }
        } else {
          toast.dismiss(); toast.error("Error while updating user's permissions!");
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    permissions?.forEach((el) => {
      const exists = userPermissions?.find((item) => item?.id === el?.id);
      if (exists) {
        setValue(`${el?.id}`, true);
      } else {
        setValue(`${el?.id}`, false);
      }
    });
  }, [userPermissions, permissions]);

  return (
    <>
      <form
        onSubmit={handleSubmit(handleSubmitUserPermissions)}
        className="user-permissions"
      >
        {permissions?.map((el, i) => {
          return (
            <div className="permission" key={i}>
              <ToggleSwitch
                name={`${el?.id}`}
                label={el?.name}
                register={register}
                required={false}
                showSVG={false}
                disabled={!userIsAllowedToAddEditUser}
              />
            </div>
          );
        })}

        <button type="submit" className="save-button">
          <p>Save</p>
        </button>
      </form>
    </>
  );
};

const UserSales = ({ userDetails }) => {
  return <Sales userDetails={userDetails} />;
};

const UserOrders = ({ userDetails }) => {
  return <Orders userDetails={userDetails} />;
};

const DeleteModel = ({ open, setOpen, selectedUser, reload }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [deleting, setDeleting] = useState(false);
  const [openAdminDelete, setOpenAdminDelete] = useState(false);

  const { id: activeUserId } = useSelector((state) => state.userReducer.value);

  const handleComplete = () => {
    dispatch(clearUser());
    dispatch(clearProducts());
    dispatch(clearAnalytics());
    dispatch(clearBranches());
    dispatch(clearCart());
    dispatch(clearHomeData());
    dispatch(clearOrganization());
    dispatch(clearSubscriptionPlans());
    storage.removeAll();
  };

  const handleConfirm = () => {
    if (selectedUser?.id) {
      setDeleting(true);

      const source = Capacitor.isNativePlatform() ? "mobile" : "web";

      deleteUserAccount(selectedUser?.id, source)
        .then((res) => {
          if (res.data?.otp) {
            setOpenAdminDelete(true);
            toast.dismiss(); toast.info(
              res.data?.message ||
                "OTP send to admin's email, Please verify it to confirm the account deletion."
            );
          } else {
            if (selectedUser?.id === activeUserId) {
              handleComplete();
            }
            reload && reload(selectedUser);
            setOpen(false);
            toast.dismiss(); toast.success(`${selectedUser?.name}'s account is deleted!`);
            !reload && navigate(ROUTES.manageUser.path);
          }
        })
        .catch((err) => {
          if (err?.response?.data?.message) {
            if (err?.response?.data?.message === "User not authorized!") {
              toast.dismiss(); toast.error(
                "You do not have sufficient permissions to perform this action. Please contact your administrator."
              );
            } else {
              toast.dismiss(); toast.error(`${err?.response?.data?.message}`);
            }
          } else {
            toast.dismiss(); toast.error(
              `${selectedUser?.name}'s account delete process failed!!`
            );
          }
        })
        .finally(() => {
          setDeleting(false);
        });
    }
  };

  const [shouldRender, setShouldRender] = useState(false);

  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (open) {
      setShouldRender(true);
      setTimeout(() => {
        setIsVisible(true);
      }, 10);
    } else if (isVisible) {
      setIsVisible(false);
      setTimeout(() => {
        setShouldRender(false);
      }, 300);
    }
  }, [open, isVisible]);

  if (!shouldRender) return null;

  if (selectedUser?.is_admin) {
    return (
      <>
        <div className={`confirm-delete-model ${isVisible ? "active" : ""}`}>
          <p className="title">Account Delete Confirmation</p>

          <div className="confirm-container">
            <img src="/images/warning.svg" alt="" aria-label="Warning" />
            <p>
              Are you sure you want to <span>DELETE</span> this account? <br />{" "}
              Since this account is the admin of the organization, deleting this
              account will cause deletion of organization data too.
            </p>
          </div>

          <div className="buttons">
            <CustomButton
              name={"Cancel"}
              color={"gray"}
              type={"button"}
              handleSubmit={() => {
                setOpen(false);
              }}
              disabled={deleting}
            />

            <CustomButton
              name={"Yes, Delete"}
              color={"danger"}
              type={"button"}
              handleSubmit={handleConfirm}
              disabled={deleting}
              loader={deleting}
            />
          </div>
        </div>
        <ConfirmAdminDelete
          open={openAdminDelete}
          setOpen={setOpenAdminDelete}
          handleComplete={handleComplete}
          user={selectedUser}
        />
      </>
    );
  } else {
    return (
      <div className={`pop-up-model ${isVisible ? "active" : ""}`}>
        <h1>Delete {selectedUser?.name} account</h1>

        <p>
          This account cannot be recovered once deleted. Are you sure you want
          to <span>delete</span> this user?
        </p>

        <div className="buttons">
          <CustomButton
            name={"Cancel"}
            color={"gray"}
            type={"button"}
            handleSubmit={() => {
              setOpen(false);
            }}
            disabled={deleting}
          />
          <CustomButton
            name={"Yes"}
            color={"danger"}
            type={"button"}
            handleSubmit={handleConfirm}
            disabled={deleting}
            loader={deleting}
          />
        </div>
      </div>
    );
  }
};

const ManageUserBranches = ({
  open,
  setOpen,
  selectedUser,
  setUserDetails,
  reload,
}) => {
  const { id: organization_id } = useSelector(
    (state) => state.organizationReducer.value
  );

  const { branches: branchList } = useSelector(
    (state) => state.branchReducer.value
  );

  const [submitting, setSubmitting] = useState(false);

  const [branches, setBranches] = useState([]);

  const [branchOptions, setBranchOptions] = useState([]);

  const [availableChanges, setAvailableChanges] = useState(false);

  useEffect(() => {
    let options = branchList?.map((el) => {
      return {
        label: el?.name,
        value: el?.id,
      };
    });
    setBranchOptions(options);
  }, [branchList]);

  useEffect(() => {
    if (selectedUser?.branches?.length > 0) {
      const userActiveOrgBranches = selectedUser?.branches?.filter(
        (el) => el?.organization_id === organization_id
      );
      setBranches(userActiveOrgBranches);
    } else {
      setBranches([]);
    }
  }, [selectedUser, open]);

  useEffect(() => {
    const userActiveOrgBranches = selectedUser?.branches?.filter(
      (el) => el?.organization_id === organization_id
    );
    let change = false;

    if (branches?.length === userActiveOrgBranches?.length) {
      for (const el of userActiveOrgBranches || []) {
        const exists = branches?.find((item) => item?.id === el?.id);
        if (exists) {
          change = false;
        } else {
          change = true;
          break;
        }
      }
      for (const el of branches || []) {
        const exists = userActiveOrgBranches?.find(
          (item) => item?.id === el?.id
        );
        if (exists) {
          change = false;
        } else {
          change = true;
          break;
        }
      }
    } else {
      change = true;
    }

    setAvailableChanges(change);
  }, [branches]);

  const handleSubmit = () => {
    if (branches?.length > 0 && selectedUser?.id) {
      const userData = branches.map((el) => {
        return el?.id;
      });

      setSubmitting(true);

      updateUserBranches(selectedUser?.id, userData)
        .then((res) => {
          toast.dismiss(); toast.success("User Branches updated sucessfully.");
          setOpen(false);
          setUserDetails((prev) => {
            return { ...prev, branches: branches };
          });
          reload && reload({ ...selectedUser, branches: branches });
        })
        .catch((err) => {
          if (err?.response?.data?.message) {
            if (err?.response?.data?.message === "User not authorized!") {
              toast.dismiss(); toast.error(
                "You do not have sufficient permissions to perform this action. Please contact your administrator."
              );
            } else {
              toast.dismiss(); toast.error(`${err?.response?.data?.message}`);
            }
          } else {
            toast.dismiss(); toast.error("Error while updating user branches!");
          }
        })
        .finally(() => {
          setSubmitting(false);
        });
    }
  };

  const addBranch = (opt) => {
    const selectedBranch = branchList?.find((el) => el?.id === opt?.value);
    const alreadyExists = branches?.find((el) => el?.id === opt?.value);
    if (selectedBranch) {
      if (alreadyExists) {
        toast.dismiss(); toast.info(`${alreadyExists?.name} already exist for this user!`);
      } else {
        const tempArray = [...branches];
        tempArray.unshift(selectedBranch);
        setBranches(tempArray);
      }
    }
  };

  const removeBranch = (index) => {
    if (branches?.length > 1) {
      const tempArray = [...branches];
      tempArray.splice(index, 1);
      setBranches(tempArray);
    } else {
      toast.dismiss(); toast.info(`User must have atleast one branch!`);
    }
  };

  const [shouldRender, setShouldRender] = useState(false);

  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (open) {
      setShouldRender(true);
      setTimeout(() => {
        setIsVisible(true);
      }, 10);
    } else if (isVisible) {
      setIsVisible(false);
      setTimeout(() => {
        setShouldRender(false);
      }, 300);
    }
  }, [open, isVisible]);

  if (!shouldRender) return null;

  return (
    <div className={`manage-user-branches ${isVisible ? "active" : ""}`}>
      <h1>Manage {selectedUser?.name}'s Branches</h1>

      <InputSelect
        options={branchOptions}
        value={{ label: "", value: "" }}
        register={() => {}}
        required={true}
        name="branch"
        handleChange={(opt) => {
          addBranch(opt);
        }}
        reset={() => {}}
        label={"Add Branch"}
        inputErrors={{}}
      />

      <div className="branch-list">
        {branches?.length > 0 ? (
          branches.map((el, i) => {
            return (
              <div className="item" key={i}>
                <p className="name">{el?.name || "Branch Name"}</p>
                <FaTrash
                  onClick={() => {
                    removeBranch(i);
                  }}
                />
              </div>
            );
          })
        ) : (
          <p style={{ color: "#888888" }}>
            User does not have any branches listed to their name.
          </p>
        )}
      </div>

      <div className="buttons">
        <CustomButton
          name={"Cancel"}
          color={"danger"}
          type={"button"}
          handleSubmit={() => {
            setOpen(false);
          }}
          disabled={submitting}
        />
        <CustomButton
          name={"Update"}
          color={"primary"}
          type={"button"}
          handleSubmit={handleSubmit}
          disabled={submitting || !availableChanges}
          loader={submitting}
        />
      </div>
    </div>
  );
};
